import {flow, getEnv, onSnapshot, types} from 'mobx-state-tree';
import * as api from '../../services/api';
import {SendBirdChatEvent} from '../../services/sb';
import {parseSbMessage} from '../../utils';
import ChatUserModel from '../ChatUserModel';
import WithFeedbackModel, {
  WithFeedbackActions
} from '../composables/withFeedbackModel';
import ThreadModel from '../ThreadModel/ThreadModel';
import ChannelMessageModel, {
  CachedMessage,
  ChannelMessage,
  InflightTextMessage,
  SyncedMessage
} from './message';
import ChannelMessagePreviewModel from './message/channelMessagePreviewModel';

export type ChatChannelViews = {
  title: string;
  sortedMessages: ChannelMessage[];
  lastMessageAt?: string;
};

export type ChatChannelActions = {
  sendTextMessage: (message: string, messageType?: string) => void;
  addInflightTextMessage: (message: string, cuid: string) => void;
  loadCachedMessages: () => void;
  loadPreviousCachedMessages: () => void;
  // tslint:disable-next-line:no-any
  receiveCachedMessages: (messages: any) => void;
  // tslint:disable-next-line:no-any
  receiveSyncedMessage: (messages: any) => void;
  addEventHandler: () => void;
  afterCreate: () => void;
};

const ChatChannelModel = types.compose(
  WithFeedbackModel,
  types
    .model({
      id: types.identifierNumber,
      name: types.string,
      type: types.string,
      url: types.string,
      messages: types.map(ChannelMessageModel),
      loading: types.optional(types.boolean, false),
      loaded: types.optional(types.boolean, false),

      // NOTE: maybe add these fields to GET /channels endpoint?
      lastMessage: types.maybeNull(ChannelMessagePreviewModel),
      unreadMessageCount: types.maybeNull(types.number),

      thread: types.maybe(ThreadModel),
      users: types.array(ChatUserModel)
    })
    .views<ChatChannelViews>(self => {
      return {
        get title() {
          const {thread, name} = self;
          return thread && thread.listing ? thread.listing.name : name;
        },
        get sortedMessages() {
          return Array.from(self.messages.values()).sort(
            (m1, m2) => m1.createdAt - m2.createdAt
          );
        },
        get lastMessageAt() {
          const {lastMessage} = self;
          return lastMessage ? lastMessage.createdAtInMs : undefined;
        }
      };
    })
    .actions<ChatChannelActions>(self => {
      const {auth} = getEnv(self);

      const channelEvent = new SendBirdChatEvent();

      // tslint:disable-next-line:no-any
      const withActions = (self as any) as ChatChannelActions;

      // tslint:disable-next-line:no-any
      const withFeedback = (self as any) as WithFeedbackActions;

      return {
        sendTextMessage: function(
          message: string,
          messageType: string = 'internal'
        ) {
          const cid = `${auth.profile.id}-${Date.now()}`;
          withActions.addInflightTextMessage(message, cid);
          api.sendTextMessage(self.id, message, cid, messageType);
        },

        addInflightTextMessage: function(message: string, cid: string) {
          self.messages.set(cid, {
            id: null,
            cid,
            format: {text: message},
            createdAt: Date.now(),
            senderId: Number(auth.profile.id),
            senderName: auth.profile.fullName || 'Not using',
            senderAvatar: auth.profile.avatar || ''
          } as InflightTextMessage);
        },

        loadCachedMessages: flow(function*() {
          const response = yield api.fetchChannelMessages(self.id);

          if (response.data && response.status === 200) {
            withActions.receiveCachedMessages(response.data.channel.messages);
          } else {
            withFeedback.sendErrorFeedback(
              'An error has occurred. Your message could not be sent.'
            );
          }
        }),

        loadPreviousCachedMessages: flow(function*() {
          self.loading = true;

          const {id, sortedMessages} = self;
          const firstMessage = sortedMessages[0];

          const response = yield api.fetchChannelMessages(
            id,
            20,
            firstMessage ? (firstMessage as CachedMessage).id : undefined
          );

          if (response.data && response.status === 200) {
            withActions.receiveCachedMessages(response.data.channel.messages);
          } else {
            withFeedback.sendErrorFeedback('Could not load messages.');
          }

          self.loading = false;
          self.loaded = true;
        }),

        // tslint:disable-next-line:no-any
        receiveCachedMessages: function(messages: any) {
          if (!messages || messages.length === 0) {
            return;
          }

          const receivedMessages = messages.reduce(
            (results: {[id: string]: CachedMessage}, m: CachedMessage) => (
              (results[String(m.id)] = m), results
            ),
            {}
          );

          self.messages.merge(receivedMessages);
        },

        // tslint:disable-next-line:no-any
        receiveSyncedMessage: function(message: any) {
          const synced = parseSbMessage(message);
          const {cid, id} = synced;

          const matchingInflight = self.messages.get(
            cid
          ) as InflightTextMessage;

          const receivingMessage = {
            ...(synced as SyncedMessage),
            createdAt: matchingInflight
              ? matchingInflight.createdAt
              : synced.createdAt
          };

          self.messages.delete(cid);
          self.messages.set(id, receivingMessage);
        },

        addEventHandler: function() {
          channelEvent.onMessageReceived = (channel, message) => {
            if (channel.url === self.url) {
              withActions.receiveSyncedMessage(message);
            }
          };
        },

        afterCreate: function() {
          withActions.addEventHandler();
          const lsMessagesKey = `channel_msg_${self.id}`;
          const locallyCachedMessages = localStorage.getItem(lsMessagesKey);

          if (locallyCachedMessages) {
            self.messages.merge(JSON.parse(locallyCachedMessages));
          }

          onSnapshot(self, snapshot => {
            localStorage.setItem(
              lsMessagesKey,
              JSON.stringify(snapshot.messages)
            );
          });
        }
      };
    })
);

export type ChatChannel = typeof ChatChannelModel.Type;

export default ChatChannelModel;



// WEBPACK FOOTER //
// ./src/models/chatChannelModel/chatChannelModel.ts